import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { acceptInvite, previewInvite, type InvitePreview } from '@/lib/auth/session';
import { clearTokens } from '@/lib/auth/token';

/**
 * 초대 수락 — `/invite/:token` (DOC-82 §3-1 · DOC-84 §3-C-2).
 *
 * <p>계정이 <b>여기서</b> 선다. 링크를 연 사람이 비밀번호를 정하면 서버가 `external_users` 행을
 * 만들고, 초대에 현장이 담겨 있었으면 배정까지 함께 선다. 수락하는 순간 로그인된 상태로 홈에 간다.
 *
 * <p><b>사유를 갈라 쓰지 않는다.</b> 만료·회수·이미 사용 — 셋 다 「이 링크는 더 이상 쓸 수
 * 없습니다」 한 줄이다. 어느 이메일이 초대됐는지, 언제 만료됐는지는 링크를 주운 사람에게 줄 정보가
 * 아니다. 자세한 사유는 서버 로그에 있다.
 *
 * <p>로그인 문지기 밖이다({@code App}). 아직 계정이 없는 사람이 두드리는 문이라서다. 다른 계정으로
 * 로그인돼 있던 기기라면(공용 PC) 그 토큰은 지운다 — 새 계정으로 갈아타는 자리다.
 */
export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [preview, setPreview] = useState<InvitePreview | null | 'failed'>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    clearTokens();
    previewInvite(token)
      .then((found) => {
        if (!alive) return;
        setPreview(found);
        setName(found.name ?? '');
      })
      .catch(() => {
        if (alive) setPreview('failed');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const mismatch = confirm !== '' && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm && !pending;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setFailed(false);
    try {
      await acceptInvite(token, { password, name: name.trim() || null, phone: phone.trim() || null });
      queryClient.clear();
      navigate('/', { replace: true });
    }
    catch {
      setFailed(true);
    }
    finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="card w-full max-w-sm">
        <div className="mb-6 text-center">
          <img src="/brand/bomi-ci.png" alt="BoMI" className="mx-auto mb-3 h-8 w-auto" />
          <div className="text-ui-lg font-semibold">협력사 포털</div>
        </div>

        {preview === null ? (
          <p className="py-6 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>불러오는 중입니다…</p>
        ) : preview === 'failed' || !preview.valid ? (
          <div className="text-center">
            <p className="mb-4 text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
              이 링크는 더 이상 쓸 수 없습니다.
            </p>
            <Link to="/login" className="text-ui-xs" style={{ color: 'var(--accent)' }}>
              로그인
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <dl className="mb-4 flex flex-col gap-1 rounded-md p-3 text-ui-xs" style={{ background: 'var(--bg-hover)' }}>
              {preview.partyName !== null && (
                <div className="flex justify-between gap-3">
                  <dt style={{ color: 'var(--text-secondary)' }}>업체</dt>
                  <dd className="min-w-0 truncate text-right">{preview.partyName}</dd>
                </div>
              )}
              {preview.email !== null && (
                <div className="flex justify-between gap-3">
                  <dt style={{ color: 'var(--text-secondary)' }}>이메일</dt>
                  <dd className="min-w-0 truncate text-right">{preview.email}</dd>
                </div>
              )}
            </dl>

            <label className="mb-3 block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>이름</span>
              <input type="text" className="input-field" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" />
            </label>
            <label className="mb-3 block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>휴대전화</span>
              <input type="tel" className="input-field" value={phone} onChange={(event) => setPhone(event.target.value)} autoComplete="tel" inputMode="tel" />
            </label>
            <label className="mb-3 block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                비밀번호 <span style={{ color: 'var(--text-muted)' }}>(8자 이상)</span>
              </span>
              <input
                type="password"
                className="input-field"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </label>
            <label className="mb-4 block">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>비밀번호 확인</span>
              <input
                type="password"
                className="input-field"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            {mismatch && (
              <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>비밀번호가 서로 다릅니다.</p>
            )}
            {failed && (
              <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>이 링크는 더 이상 쓸 수 없습니다.</p>
            )}

            <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
              {pending ? '만드는 중입니다…' : '가입하고 시작'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
